import { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { FaAngleLeft, FaAngleRight, FaEdit } from "react-icons/fa";
import { PAGE_SIZE } from "../Utils/constant";
import TextInput from "../common/TextInput";
import { supabase } from "../../supabaseClient";

const DriverMedicalNew = () => {
  const [page, setPage] = useState(1);
  const [isEdit, setIsEdit] = useState(false);
  const [medical, setMedical] = useState(null);
  const [search, setSearch] = useState(null);
  const [medicalData, setMedicalData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [drivers, setDrivers] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      DriverID: "",
      EvaluationDate: "",
      EvaluationNotes: "",
    },
  });

  useEffect(() => {
    if (medical) {
      reset({
        DriverID: medical.DriverID,
        EvaluationDate: medical.EvaluationDate
          ? medical.EvaluationDate.split("T")[0]
          : "",
        EvaluationNotes: medical.EvaluationNotes,
      });
    }
  }, [medical, reset]);

  useEffect(() => {
    const fetchDrivers = async () => {
      const { data: Drivers, error: dError } = await supabase
        .from("driver_auth")
        .select(
          `
          id,
          email,
          is_active,
          driver_profiles (
            id,
            first_name,
            last_name
          )
        `
        )
        .eq("is_active", true);

      if (!dError) {
        // Transform data to match expected format
        const transformedDrivers =
          Drivers?.map((driver) => ({
            DriverID: driver.id,
            FirstName: driver.driver_profiles?.[0]?.first_name || "",
            LastName: driver.driver_profiles?.[0]?.last_name || "",
            Email: driver.email,
          })) || [];

        setDrivers(transformedDrivers);
      }
    };
    fetchDrivers();
  }, []);

  useEffect(() => {
    const fetchMedical = async () => {
      let query = supabase
        .from("DriverMedical")
        .select("DriverMedicalID, DriverID, EvaluationDate, EvaluationNotes", {
          count: "exact",
        })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      if (search && search.text) {
        query = query.ilike(search.type, `%${search.text}%`);
      }
      const { data, count, error } = await query;
      if (!error) {
        setMedicalData(data);
        setTotalCount(count);
      }
    };
    fetchMedical();
  }, [page, search, isEdit]);

  const onNextPage = useCallback(() => {
    setPage((old) => old + 1);
  }, []);
  const onPrevPage = useCallback(() => {
    setPage((old) => Math.max(old - 1, 1));
  }, []);

  const onSuccess = useCallback(() => {
    toast.success("Success");
    setIsEdit(false);
    setMedical(null);
  }, []);
  const onError = useCallback((err) => {
    toast.error("Error: " + err.message);
  }, []);

  const onAddNew = () => {
    reset({
      DriverID: drivers[0] ? drivers[0].DriverID : "",
      EvaluationDate: "",
      EvaluationNotes: "",
    });
    setIsEdit(true);
    setMedical(null);
  };

  const OnCancel = () => {
    setIsEdit(false);
    setMedical(null);
  };

  const OnCreate = useCallback(
    async (data) => {
      if (medical) {
        // Update
        const { error } = await supabase
          .from("DriverMedical")
          .update({
            DriverID: data.DriverID,
            EvaluationDate: data.EvaluationDate,
            EvaluationNotes: data.EvaluationNotes,
          })
          .eq("DriverMedicalID", medical.DriverMedicalID);
        if (error) onError(error);
        else onSuccess();
      } else {
        // Create
        const { error } = await supabase.from("DriverMedical").insert({
          DriverID: data.DriverID,
          EvaluationDate: data.EvaluationDate,
          EvaluationNotes: data.EvaluationNotes,
        });
        if (error) onError(error);
        else onSuccess();
      }
    },
    [medical, onError, onSuccess]
  );

  const OnSearch = useCallback((data) => {
    setSearch(data);
  }, []);

  return (
    <>
      {isEdit ? (
        <div className="driver-patent-edit-content mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>Driver</div>
            <div className="w-35">
              <Controller
                control={control}
                name="DriverID"
                rules={{ required: "Required" }}
                render={({ field: { value, onChange } }) => (
                  <select
                    className="custom-input rounded-lg"
                    value={value}
                    onChange={onChange}
                    error={errors.DriverID?.message}
                  >
                    {drivers.map((driver) => (
                      <option key={driver.DriverID} value={driver.DriverID}>
                        {driver.FirstName} {driver.LastName}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Evaluation Date</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="EvaluationDate"
                rules={{ required: "Evaluation Date is required" }}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                  />
                )}
              />
            </div>
          </div>
          <div className="flex justify-between mb-2">
            <div>
              <p>Evaluation Notes</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="EvaluationNotes"
                rules={{ required: "Evaluation Notes are required" }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.EvaluationNotes?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex gap-4 mt-4 save-button">
            <button
              disabled={false}
              onClick={handleSubmit(OnCreate)}
              className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
            >
              <span>{medical ? "Update" : "Create"}</span>
            </button>
            <div>
              <div
                onClick={OnCancel}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Cancel</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div className="flex ">
            <h5 className="mb-0">Driver Medical</h5>
            <button
              className="justify-between rounded-lg ml-1 flex items-center custom-blue-button border-none"
              onClick={onAddNew}
            >
              <span>Add New</span>
            </button>
          </div>
          <SearchBar setSearch={setSearch} OnSearch={OnSearch} />
          <table className="table-auto w-full mt-4 border border-gray-200">
            <thead>
              <tr className="bg-gray-100 cursor-pointer">
                <th className="px-4 py-2 border">ID</th>
                <th className="px-4 py-2 border">Driver</th>
                <th className="px-4 py-2 border">Evaluation Date</th>
                <th className="px-4 py-2 border">Evaluation Notes</th>
                <th className="px-4 py-2 border">Edit</th>
              </tr>
            </thead>
            <tbody>
              {medicalData &&
                medicalData.map((type) => (
                  <tr key={type.DriverMedicalID} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">{type.DriverMedicalID}</td>
                    <td className="px-4 py-2 border">
                      {drivers.find((d) => d.DriverID === type.DriverID)
                        ? `${
                            drivers.find((d) => d.DriverID === type.DriverID)
                              .FirstName
                          } ${
                            drivers.find((d) => d.DriverID === type.DriverID)
                              .LastName
                          }`
                        : ""}
                    </td>
                    <td className="px-4 py-2 border">
                      {type.EvaluationDate
                        ? type.EvaluationDate.split("T")[0]
                        : ""}
                    </td>
                    <td className="px-4 py-2 border">{type.EvaluationNotes}</td>
                    <td className="px-4 py-2 border cursor-point">
                      <FaEdit
                        onClick={() => {
                          setMedical(type);
                          setIsEdit(true);
                        }}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * PAGE_SIZE + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * PAGE_SIZE)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * PAGE_SIZE > totalCount}
                    className={`pagination-item ${
                      page * PAGE_SIZE > totalCount ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DriverMedicalNew;

const SearchBar = ({ setSearch, OnSearch }) => {
  const {
    control: searchControl,
    handleSubmit: handleSearchSubmit,
    reset: searchReset,
  } = useForm({
    defaultValues: {
      type: "EvaluationNotes",
      text: "",
    },
  });

  return (
    <div className="flex mt-4 items-center gap-4 condition-bar">
      <div className="flex items-center">
        <p className="mb-0">Look For:</p>
        <div className="flex justify-between w-10 ml-1">
          <Controller
            control={searchControl}
            name="type"
            render={({ field: { value, onChange } }) => (
              <select
                className="custom-input rounded-lg cursor-pointer"
                value={value}
                onChange={onChange}
              >
                <option value={"EvaluationNotes"}>Evaluation Notes</option>
                <option value={"EvaluationDate"}>Evaluation Date</option>
              </select>
            )}
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Controller
          control={searchControl}
          name="text"
          rules={{ required: "Required" }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={value}
              onChange={onChange}
              className="custom-input rounded-lg w-100 "
            />
          )}
        />
      </div>
      <div className="flex">
        <div>
          <button
            onClick={handleSearchSubmit(OnSearch)}
            className="justify-between rounded-lg flex items-center w-30 custom-search-button border-none"
          >
            <span>Search</span>
          </button>
        </div>
        <div>
          <button
            onClick={() => {
              searchReset({ type: "EvaluationNotes", text: "" });
              setSearch(null);
            }}
            className="justify-between rounded-lg ml-1 flex items-center custom-reset-button"
          >
            <span>Reset</span>
          </button>
        </div>
      </div>
    </div>
  );
};
